/* eslint-disable no-console, @typescript-eslint/no-explicit-any */
"use client";

import {useChat} from '@ai-sdk/react';
import Cookies from 'js-cookie';
import {ArrowUp, ChevronDown, CircleUser, CopyIcon, Download, FileText, Image as ImageIcon, Loader2Icon, Paperclip, RefreshCcw, Square,} from "lucide-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex';
import remarkBreaks from "remark-breaks";
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import {FilePreview} from "@components/FilePreview";
import {PreviewCard} from "@components/forecast/PreviewCard";
import {Avatar, AvatarFallback} from "@components/ui/avatar";
import {Button} from "@components/ui/button";
import {ChatBubble, ChatBubbleAction, ChatBubbleMessage} from "@components/ui/chat/chat-bubble"
import {ChatInput} from "@components/ui/chat/chat-input";
import {ChatMessageList} from "@components/ui/chat/chat-message-list"
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@components/ui/collapsible";
import {GenericReportCard} from "@components/vnext/GenericReportCard";
import {detectVisualIntent, shouldRenderVisual} from "@components/vnext/visual-intent";
import {isValidVisualReport} from "@components/vnext/visual-report";
import {Agent} from "@contexts/AgentContext";
import {commitCashFlowOps} from "@hooks/use-cashflow-v2";
import {useFileUpload} from "@hooks/use-file-upload";
import {commitOps} from "@hooks/use-forecast-v2";
import {preprocessLaTeX} from "@lib/utils.ts";

import type Message from "ai";

import 'katex/dist/katex.css'

const thinkingWords = [
    "Analyzing",
    "Computing",
    "Calculating",
    "Evaluating",
    "Examining",
    "Investigating",
    "Assessing",
    "Diagnosing",
    "Synthesizing",
    "Reasoning",
    "Deducing",
    "Inferring",
    "Correlating",
    "Optimizing"
];

const getRandomThinkingWord = () => {
    return thinkingWords[Math.floor(Math.random() * thinkingWords.length)];
};

const fadeToNewWord = (setCurrentThinkingWord: (word: string) => void, setIsWordFading: (fading: boolean) => void) => {
    // Start fade out
    setIsWordFading(true);

    // After 250ms (half of 0.5s), change word and start fade in
    setTimeout(() => {
        setCurrentThinkingWord(getRandomThinkingWord());
        setIsWordFading(false);
    }, 250);
};

const humanizeToolName = (toolName: string) => {
    return toolName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/** VNext tools that genuinely mutate data and should trigger UI refresh.
 *  Read-only tools (run_checks, run_reconciliation, run_accuracy_audit) are
 *  excluded — they only read data and triggering a full workspace invalidation
 *  causes a request storm that overwhelms the backend. */
const VNEXT_MUTATING_TOOLS = new Set([
    "vnext_commit_ops",
    "vnext_seed_from_xero",
    "vnext_change_baseline",
    "vnext_delete_op",
    "vnext_sync_xero",
    "vnext_update_contact_mapping",
    "vnext_bulk_assign_segment",
    "vnext_update_account_group_mapping",
    "vnext_auto_map_account_groups",
    "vnext_sandbox_analyze",
    "vnext_sandbox_create_rich_report",
    "vnext_sandbox_update_artifact",
    "vnext_sandbox_delete_artifact",
    "vnext_sandbox_pin_artifact",
    "vnext_sandbox_reorder_artifacts",
    "vnext_extract_assumptions",
    "vnext_weekly_forecast_override",
    "vnext_purge_revision",
    "vnext_create_scenario",
]);

const toolActionSummary = (toolName: string, args: Record<string, any>): string => {
    switch (toolName) {
        case "get_forecast":
        case "get_pnl_forecast":
            return "Fetched P&L forecast data";
        case "get_cashflow_forecast":
            return "Fetched cash flow forecast data";
        case "preview_ops":
            return `Previewed ${args?.ops?.length ?? 1} P&L adjustment(s)`;
        case "preview_cashflow_ops":
            return `Previewed ${args?.ops?.length ?? 1} cash flow adjustment(s)`;
        case "commit_ops":
            return `Committed ${args?.ops?.length ?? 1} P&L adjustment(s)`;
        case "commit_cashflow_ops":
            return `Committed ${args?.ops?.length ?? 1} cash flow adjustment(s)`;
        case "create_scenario":
            return `Created scenario "${args?.name || "new"}"`;
        case "control_dashboard":
            return "Updated dashboard view";
        case "get_dashboard_data":
            return "Fetched dashboard summary";
        case "classify_and_preview":
            return "Classified intent and previewed adjustments";
        case "export_dashboard":
            return "Exported dashboard report";
        case "vnext_run_checks":
            return "Ran quality checks";
        case "vnext_get_checks":
            return "Fetched check results";
        case "vnext_get_model":
            return "Fetched financial model";
        case "vnext_preview_ops":
            return `Previewed ${args?.ops?.length ?? 1} forecast adjustment(s)`;
        case "vnext_commit_ops":
            return `Committed ${args?.ops?.length ?? 1} forecast adjustment(s)`;
        case "vnext_get_knowledge_documents":
            return "Listed knowledge documents";
        case "vnext_list_nodes":
            return "Listed model nodes";
        case "vnext_get_summary":
            return "Fetched financial summary";
        case "vnext_extract_assumptions":
            return "Extracted assumptions from documents";
        default:
            return humanizeToolName(toolName);
    }
};

type ParsedToolResult = Record<string, any> | null;

interface DownloadInfo {
    url: string;
    filename: string;
}

const parseToolResult = (rawResult: unknown): ParsedToolResult => {
    let outer: unknown = rawResult;

    if (typeof outer === "string") {
        try {
            outer = JSON.parse(outer);
        } catch {
            return null;
        }
    }
    if (!outer || typeof outer !== "object") return null;

    const maybeData = (outer as Record<string, unknown>).data;
    if (typeof maybeData === "string") {
        try {
            const parsedInner = JSON.parse(maybeData);
            if (parsedInner && typeof parsedInner === "object") return parsedInner as Record<string, any>;
        } catch {
            // Keep outer object when inner payload is non-JSON text.
        }
    } else if (maybeData && typeof maybeData === "object") {
        return maybeData as Record<string, any>;
    }

    return outer as Record<string, any>;
};

const filenameFromUrl = (url: string): string => {
    const clean = url.split("?")[0];
    const last = clean.split("/").filter(Boolean).pop();
    return last || "download";
};

const extractDownloadInfos = (parsed: ParsedToolResult): DownloadInfo[] => {
    if (!parsed) return [];

    const candidates: Array<Record<string, any>> = [parsed];
    const listKeys = ["generated_files", "files", "artifacts", "outputs"];
    for (const key of listKeys) {
        const value = parsed[key];
        if (Array.isArray(value)) {
            for (const item of value) {
                if (item && typeof item === "object") candidates.push(item as Record<string, any>);
            }
        }
    }

    const seen = new Set<string>();
    const links: DownloadInfo[] = [];
    for (const item of candidates) {
        const url = typeof item.download_url === "string"
            ? item.download_url
            : typeof item.file_id === "string"
                ? `/api/files/download/${item.file_id}/`
                : null;
        if (!url || seen.has(url)) continue;
        seen.add(url);
        links.push({
            url,
            filename:
                (typeof item.filename === "string" && item.filename) ||
                filenameFromUrl(url),
        });
    }

    return links;
};

function DownloadLink({url, filename}: DownloadInfo) {
    const [state, setState] = useState<"idle" | "loading" | "error">("idle");

    const handleClick = useCallback(async (e: React.MouseEvent) => {
        e.preventDefault();
        setState("loading");
        try {
            const resp = await fetch(url, {credentials: "include"});
            if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
            setState("idle");
        } catch {
            setState("error");
        }
    }, [url, filename]);

    if (state === "loading") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded border bg-background text-muted-foreground ml-1">
                <Loader2Icon className="size-3 animate-spin" />
                {filename}
            </span>
        );
    }

    if (state === "error") {
        return (
            <button
                onClick={handleClick}
                className="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 transition-colors ml-1"
            >
                <RefreshCcw className="size-3" />
                Retry {filename}
            </button>
        );
    }

    return (
        <button
            onClick={handleClick}
            className="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded border bg-background hover:bg-accent transition-colors ml-1"
        >
            <Download className="size-3" />
            {filename}
        </button>
    );
}

interface ChatInterfaceProps {
    agent: Agent;
    loading?: boolean;
    containerMode?: "standalone" | "floating" | "fullscreen";
}

export function ChatInterface({agent, loading = false, containerMode = "standalone"}: ChatInterfaceProps) {
    const csrfToken = Cookies.get('csrftoken') || '';

    // const agentName = agent?.name || 'Pecunio Agent';
    const agentWelcomeMessage = agent?.welcomeMessage || `Hello! I'm your Pecunio Agent. How can I help you today?`;

    const defaultMessages: Message.Message[] = [
        {
            id: "1",
            role: 'assistant',
            content: agentWelcomeMessage,
        },
    ];

    const actionIcons = [
        {
            icon: CopyIcon,
            label: "Copy",
        },
        {
            icon: RefreshCcw,
            label: "Refresh",
        }
    ];

    const [isGenerating, setIsGenerating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [currentThinkingWord, setCurrentThinkingWord] = useState(getRandomThinkingWord());
    const [isWordFading, setIsWordFading] = useState(false);
    const dashboardContextRef = useRef<Record<string, unknown> | null>(null);
    const [messageAttachments, setMessageAttachments] = useState<import("@hooks/use-file-upload").UploadedFile[][]>([]);
    const {files, isUploading, fileInputRef, handleFileSelect, removeFile, clearFiles, openFilePicker} = useFileUpload();
    const {
        messages,
        status,
        input,
        handleInputChange,
        handleSubmit,
        reload,
        stop,
    } = useChat({
        id: agent?.slug,
        api: `/api/agents/${agent?.slug}/chat/`,
        headers: {
            "X-CSRFToken": csrfToken,
        },
        initialMessages: defaultMessages,
        // transform: smoothStream({
        //     delayInMs: 20, // optional: defaults to 10ms
        //     chunking: 'line', // optional: defaults to 'word'
        // }),
        experimental_throttle: 50,
        onResponse(response) {
            if (response.status == 200) {
                console.log(response);
                setIsGenerating(false);
            } else if (response.status == 403 || response.status == 401) {
                setSessionExpired(true);
                setIsGenerating(false);
            } else {
                setIsGenerating(false);
                // Try to extract a meaningful error from the response body
                response.clone().text().then((body) => {
                    let msg = response.statusText || `Error ${response.status}`;
                    try {
                        const json = JSON.parse(body);
                        if (json.errors?.[0]) msg = json.errors[0];
                        else if (json.error) msg = json.error;
                        else if (json.detail) msg = json.detail;
                    } catch { /* not JSON, use statusText */ }
                    setErrorMessage(`${msg} — please try again.`);
                }).catch(() => {
                    setErrorMessage(`Error ${response.status} — please try again.`);
                });
            }
        },
        onError(error) {
            if (error) {
                setIsGenerating(false);
                const msg = error instanceof Error ? error.message : "Something went wrong";
                setErrorMessage(`${msg} — please try again.`);
                console.error("Error:", error);
            }
        },
    });

    const messagesRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [messages]);

    // Dispatch CustomEvent when control_dashboard tool result arrives
    useEffect(() => {
        for (const message of messages) {
            if (!message.parts) continue;
            for (const part of message.parts) {
                if (part.type !== "tool-invocation" || part.toolInvocation.state !== "result") continue;
                const toolName = part.toolInvocation.toolName;
                if (toolName === "control_dashboard") {
                    try {
                        const action = parseToolResult(part.toolInvocation.result);
                        if (action?.__dashboard_action__) {
                            window.dispatchEvent(
                                new CustomEvent("dashboard-action", {detail: action})
                            );
                        }
                    } catch {
                        // ignore parse errors
                    }
                }
                // Invalidate forecast caches when agent writes (v2 tools)
                if (
                    toolName === "commit_ops" ||
                    toolName === "preview_ops" ||
                    toolName === "get_forecast" ||
                    toolName === "create_scenario" ||
                    toolName === "commit_cashflow_ops" ||
                    toolName === "preview_cashflow_ops" ||
                    toolName === "get_cashflow_forecast" ||
                    toolName === "classify_and_preview" ||
                    // Legacy v1 tools
                    toolName === "upsert_forecast_assumptions" ||
                    toolName === "add_forecast_note" ||
                    toolName === "generate_executive_summary"
                ) {
                    window.dispatchEvent(new CustomEvent("forecast-session-updated"));
                }
                // Invalidate vnext model caches when agent writes (vnext tools)
                if (VNEXT_MUTATING_TOOLS.has(toolName)) {
                    window.dispatchEvent(new CustomEvent("vnext-data-updated"));
                }
                // VNext UI control — dispatch to WorkspaceShell
                if (toolName === "vnext_control_ui") {
                    try {
                        const action = parseToolResult(part.toolInvocation.result);
                        if (action?.__vnext_ui_action__) {
                            window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: action}));
                        }
                    } catch { /* ignore */ }
                }
                // Dispatch forecast-committed with delta details for card flashing
                if (toolName === "commit_ops" || toolName === "commit_cashflow_ops") {
                    try {
                        const parsed = parseToolResult(part.toolInvocation.result);
                        if (parsed?.committed) {
                            window.dispatchEvent(new CustomEvent("forecast-committed", {
                                detail: {
                                    domain: toolName === "commit_cashflow_ops" ? "cashflow" : "pnl",
                                    ops_count: parsed.ops_count,
                                    committed_op_ids: parsed.committed_op_ids,
                                },
                            }));
                        }
                    } catch {
                        // ignore
                    }
                }
            }
        }
    }, [messages]);

    // Capture dashboard data for agent context
    useEffect(() => {
        const handler = (e: Event) => {
            dashboardContextRef.current = (e as CustomEvent).detail;
        };
        window.addEventListener("dashboard-data-loaded", handler);
        return () => window.removeEventListener("dashboard-data-loaded", handler);
    }, []);

    // Listen for agent-auto-submit events (e.g. from Build Model button)
    useEffect(() => {
        const handler = (e: Event) => {
            const message = (e as CustomEvent).detail?.message;
            if (message && formRef.current) {
                handleInputChange({target: {value: message}} as React.ChangeEvent<HTMLTextAreaElement>);
                // Submit on next tick after input is set
                setTimeout(() => {
                    formRef.current?.requestSubmit();
                }, 0);
            }
        };
        window.addEventListener("agent-auto-submit", handler);
        return () => window.removeEventListener("agent-auto-submit", handler);
    }, [handleInputChange]);

    // Cycle through thinking words every 4 seconds while streaming
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (status === 'streaming') {
            interval = setInterval(() => {
                fadeToNewWord(setCurrentThinkingWord, setIsWordFading);
            }, 4000); // Change word every 4 seconds
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [status]);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!input && files.length === 0) return;
        setIsGenerating(true);
        setErrorMessage(null);
        setCurrentThinkingWord(getRandomThinkingWord());
        setMessageAttachments(prev => [...prev, files.length > 0 ? [...files] : []]);
        handleSubmit(e, {
            allowEmptySubmit: files.length > 0,
            body: {
                fileIds: files.map(f => f.id),
                ...(dashboardContextRef.current ? {dashboardContext: dashboardContextRef.current} : {}),
            },
        });
        clearFiles();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (isGenerating || status === `streaming` || (!input && files.length === 0)) return;
            setIsGenerating(true);
            setCurrentThinkingWord(getRandomThinkingWord());
            void onSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
        }
    };

    const handleActionClick = async (action: string, messageIndex: number) => {
        console.log("Action clicked:", action, "Message index:", messageIndex);
        if (action === "Refresh") {
            setIsGenerating(true);
            setErrorMessage(null);
            try {
                await reload({
                    body: {
                        ...(dashboardContextRef.current ? {dashboardContext: dashboardContextRef.current} : {}),
                    },
                });
            } catch (error) {
                console.error("Error reloading:", error);
            } finally {
                setIsGenerating(false);
            }
        }

        if (action === "Copy") {
            const message = messages[messageIndex];
            if (message && message.role === "assistant") {
                await navigator.clipboard.writeText(message.content);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center flex-1">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p>Loading agent...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`flex flex-col w-full ${
                containerMode === "floating"
                    ? "h-full"
                    : containerMode === "fullscreen"
                        ? "h-full max-w-4xl mx-auto px-8 py-4"
                        : "max-w-6xl mx-auto lg:p-8 md:p-6 p-2 viewport-height-minus-spacing"
            }`}>
            <div className="flex-grow overflow-y-auto min-h-0">
                <ChatMessageList ref={messagesRef}>
                    {messages.map((message, index) => {
                        const variant = message.role === 'user' ? 'sent' : 'received';
                        return (
                            <ChatBubble key={message.id} variant={variant}
                                        className={"flex row " + (index === 0 ? "items-center" : "items-end")}
                                        layout={message.role === 'assistant' ? 'ai' : 'default'}>
                                {variant === 'sent' &&
                                    <div className={message.role === 'assistant' && index > 0 ? "pb-12" : ""}>
                                        <Avatar className="bg-primary">
                                            <AvatarFallback>
                                                <CircleUser
                                                    className="text-black dark:text-white"/></AvatarFallback>
                                        </Avatar>
                                    </div>}
                                <ChatBubbleMessage>
                                    <div className='w-full flex flex-row items-start'>
                                        {
                                            message.role === 'assistant' && index !== 0 && index === messages.length - 1 && status === 'streaming' && (
                                                <div className='text-xs py-4 flex flex-row'>
                                                    <svg aria-hidden="true"
                                                         className="w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-gray-600 mr-2"
                                                         viewBox="0 0 100 101" fill="none"
                                                         xmlns="http://www.w3.org/2000/svg">
                                                        <path
                                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                            fill="currentColor"/>
                                                        <path
                                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                            fill="currentFill"/>
                                                    </svg>
                                                    <span style={{
                                                        opacity: isWordFading ? 0 : 1,
                                                        transition: 'opacity 0.25s ease-in-out'
                                                    }}>
                                                            {currentThinkingWord}...
                                                        </span></div>)
                                        }
                                    </div>

                                    {(() => {
                                        // Show attached files for user messages
                                        if (message.role === 'user') {
                                            const userMsgIndex = messages.slice(0, index + 1).filter(m => m.role === 'user').length - 1;
                                            const attachments = messageAttachments[userMsgIndex] || [];
                                            if (attachments.length > 0) {
                                                return (
                                                    <>
                                                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                                                            {attachments.map(file => (
                                                                <div key={file.id} className="inline-flex items-center gap-1 bg-primary/10 rounded px-2 py-0.5 text-xs">
                                                                    {file.isImage ? <ImageIcon className="size-3 shrink-0"/> : <FileText className="size-3 shrink-0"/>}
                                                                    <span className="max-w-32 truncate">{file.filename}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {message.content && (
                                                            <div className="prose-sm whitespace-normal dark:prose prose-invert">
                                                                <Markdown remarkPlugins={[remarkBreaks, remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                                                    {preprocessLaTeX(message.content)}
                                                                </Markdown>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            }
                                        }
                                        return null;
                                    })()}

                                    {(() => {
                                        // Separate text and tool parts
                                        const textParts = message.parts.filter(part => part.type === 'text');
                                        const toolParts = message.parts.filter(part => part.type === 'tool-invocation');

                                        return (
                                            <>
                                                {/* Render text parts first */}
                                                {textParts.map(part => (
                                                    <div
                                                        className={"prose-sm whitespace-normal" + (variant === 'received' ? ' dark:prose-invert prose' : ' dark:prose prose-invert')}
                                                        key={part.text.slice(0, 100)}>
                                                        <Markdown
                                                            remarkPlugins={[remarkBreaks, remarkGfm, remarkMath]}
                                                            rehypePlugins={[rehypeKatex]}
                                                        >{preprocessLaTeX(part.text)}</Markdown>
                                                    </div>
                                                ))}

                                                {/* Decision log — compact tool summaries */}
                                                {toolParts.length > 0 && (
                                                    <div className="my-2 bg-gray-100 p-2 rounded-md dark:bg-gray-800 text-xs space-y-1">
                                                        {toolParts.map(part => {
                                                            const callId = part.toolInvocation.toolCallId;
                                                            const toolName = part.toolInvocation.toolName;
                                                            const isPending = part.toolInvocation.state === 'call' || part.toolInvocation.state === 'partial-call';
                                                            const args = (part.toolInvocation.args || {}) as Record<string, any>;

                                                            const parsedResult = !isPending && 'result' in part.toolInvocation
                                                                ? parseToolResult(part.toolInvocation.result)
                                                                : null;
                                                            const downloadInfos = extractDownloadInfos(parsedResult);

                                                            return (
                                                                <div key={callId} className="flex items-center gap-1.5 flex-wrap">
                                                                    <span>{isPending ? '⏳' : '✅'}</span>
                                                                    <span className="text-muted-foreground">
                                                                        {isPending
                                                                            ? <>Running <strong>{humanizeToolName(toolName)}</strong>...</>
                                                                            : toolActionSummary(toolName, args)}
                                                                    </span>
                                                                    {downloadInfos.map((downloadInfo) => (
                                                                        <DownloadLink key={downloadInfo.url} url={downloadInfo.url} filename={downloadInfo.filename} />
                                                                    ))}
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Technical details — raw JSON behind collapsible */}
                                                        {toolParts.some(p => p.toolInvocation.state !== 'call' && p.toolInvocation.state !== 'partial-call') && (
                                                            <Collapsible className="pt-1">
                                                                <CollapsibleTrigger className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                                                                    <ChevronDown className="size-3"/>
                                                                    <span>Technical details</span>
                                                                </CollapsibleTrigger>
                                                                <CollapsibleContent>
                                                                    <div className="mt-1 flex flex-col gap-2 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                                                                        {toolParts.filter(p => p.toolInvocation.state !== 'call' && p.toolInvocation.state !== 'partial-call').map(part => {
                                                                            let resultDisplay = "";
                                                                            let argsDisplay = "";
                                                                            const inv = part.toolInvocation as any;
                                                                            const parsed = parseToolResult(inv.result);
                                                                            try {
                                                                                resultDisplay = parsed
                                                                                    ? JSON.stringify(parsed, null, 2)
                                                                                    : String(inv.result ?? "");
                                                                                argsDisplay = JSON.stringify(inv?.args || {}, null, 2);
                                                                            } catch {
                                                                                resultDisplay = String(inv.result ?? "");
                                                                            }
                                                                            return (
                                                                                <Collapsible key={part.toolInvocation.toolCallId + '-detail'}>
                                                                                    <CollapsibleTrigger className="flex items-center gap-1">
                                                                                        <ChevronDown className="size-3"/>
                                                                                        <strong>{humanizeToolName(part.toolInvocation.toolName)}</strong>
                                                                                    </CollapsibleTrigger>
                                                                                    <CollapsibleContent>
                                                                                        <div className="prose prose-sm dark:prose-invert overflow-y-auto overflow-x-auto max-h-32 w-full pl-4 mt-1">
                                                                                            <Markdown>{`\`\`\`json\n${argsDisplay}\n\`\`\``}</Markdown>
                                                                                            <Markdown>{`\`\`\`json\n${resultDisplay}\n\`\`\``}</Markdown>
                                                                                        </div>
                                                                                    </CollapsibleContent>
                                                                                </Collapsible>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </CollapsibleContent>
                                                            </Collapsible>
                                                        )}
                                                    </div>
                                                )}

                                                {/* VNext rich tool cards (schema-driven → legacy fallback, gated by visual intent) */}
                                                {(() => {
                                                    // Find the last user message before this assistant message
                                                    const lastUserMsg = messages.slice(0, index).reverse().find(m => m.role === "user");
                                                    const userText = typeof lastUserMsg?.content === "string" ? lastUserMsg.content : "";
                                                    const intent = detectVisualIntent(userText);

                                                    return toolParts.map(part => {
                                                        if (part.toolInvocation.state === "call" || part.toolInvocation.state === "partial-call") return null;
                                                        try {
                                                            const parsed = parseToolResult(part.toolInvocation.result);
                                                            if (!parsed) return null;
                                                            const toolName = part.toolInvocation.toolName;
                                                            const hasVR = isValidVisualReport(parsed?.visual_report);
                                                            const suggestVisual = parsed?.suggest_visual === true;
                                                            const shouldRender = hasVR && shouldRenderVisual(intent, suggestVisual);
                                                            // Debug: instrument VNext tool routing (dev only)
                                                            if (import.meta.env.DEV && toolName.startsWith("vnext_")) {
                                                                console.debug("[VNext card]", {toolName, hasVisualReport: hasVR, suggestVisual, intent, shouldRender});
                                                            }
                                                            // 1. Schema-driven: visual_report present + gating check
                                                            if (hasVR && shouldRender) {
                                                                return <div key={part.toolInvocation.toolCallId + "-vr"}><GenericReportCard report={parsed.visual_report} /></div>;
                                                            }
                                                            return null;
                                                        } catch { return null; }
                                                    });
                                                })()}

                                                {/* Render PreviewCard for preview_ops / preview_cashflow_ops results */}
                                                {toolParts.map(part => {
                                                    const tn = part.toolInvocation.toolName;
                                                    const isCashflow = tn === "preview_cashflow_ops";
                                                    if (
                                                        (tn !== "preview_ops" && tn !== "preview_cashflow_ops") ||
                                                        part.toolInvocation.state === "call" ||
                                                        part.toolInvocation.state === "partial-call"
                                                    ) return null;
                                                    try {
                                                        const parsed = parseToolResult(part.toolInvocation.result);
                                                        if (!parsed?.summary_deltas || !parsed?.deltas) return null;
                                                        const args = part.toolInvocation.args as Record<string, any>;
                                                        const opsPayload = args?.ops;
                                                        const scenarioId = parsed?.scenario?.id || args?.scenario_id;
                                                        const commitFn = isCashflow ? commitCashFlowOps : commitOps;
                                                        return (
                                                            <PreviewCard
                                                                key={part.toolInvocation.toolCallId + "-preview"}
                                                                summaryDeltas={parsed.summary_deltas}
                                                                deltas={parsed.deltas}
                                                                opsCount={parsed.ops_previewed ?? 1}
                                                                domain={isCashflow ? "cashflow" : "pnl"}
                                                                onCommit={opsPayload ? async () => {
                                                                    try {
                                                                        await commitFn(opsPayload, scenarioId);
                                                                        window.dispatchEvent(new CustomEvent("forecast-session-updated"));
                                                                        window.dispatchEvent(new CustomEvent("forecast-committed", {
                                                                            detail: {domain: isCashflow ? "cashflow" : "pnl"},
                                                                        }));
                                                                    } catch (err) {
                                                                        console.error("Commit from preview failed:", err);
                                                                    }
                                                                } : undefined}
                                                            />
                                                        );
                                                    } catch {
                                                        return null;
                                                    }
                                                })}
                                            </>
                                        );
                                    })()}
                                    {message.role === 'assistant' && status !== 'streaming' && index > 0 && (
                                        <div>
                                            {actionIcons.map(({icon: Icon, label}) => (
                                                <ChatBubbleAction
                                                    className="size-6"
                                                    key={label}
                                                    icon={<Icon className="size-3"/>}
                                                    onClick={() => handleActionClick(label, index)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </ChatBubbleMessage>
                            </ChatBubble>
                        )
                    })}
                </ChatMessageList>
            </div>

            {sessionExpired && (
                <div className="text-xs text-center py-3 bg-amber-50 border border-amber-200 rounded-md mx-4">
                    <span className="text-amber-700">Session expired.</span>{" "}
                    <a
                        href={`/admin/login/?next=/agents/${agent.slug}`}
                        className="text-[#209DB3] hover:underline font-medium"
                    >
                        Log in
                    </a>
                </div>
            )}

            {errorMessage && (
                <div className="text-xs text-red-500 text-center py-2">
                    {errorMessage}
                </div>
            )}

            <div className="flex row justify-center w-full pt-4 flex-shrink-0">
                <form
                    ref={formRef}
                    onSubmit={onSubmit}
                    data-tour="agent-panel-composer"
                    className={`relative rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring shadow-sm w-full ${containerMode === "fullscreen" ? "" : "max-w-3xl"}`}
                >
                    <FilePreview files={files} onRemove={removeFile}/>
                    <ChatInput
                        value={input}
                        onKeyDown={onKeyDown}
                        onChange={handleInputChange}
                        placeholder="Reply to Pecunio..."
                        className="rounded-lg bg-background border-0 shadow-none focus-visible:ring-0"
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.xlsx,.xls,.csv,.tsv,.txt,.md,.png,.jpg,.jpeg,.gif,.webp"
                        onChange={handleFileSelect}
                        multiple
                    />
                    <div className="flex items-center gap-x-2 pt-0 p-2 md:p-3">
                        <Button
                            onClick={openFilePicker}
                            variant="outline"
                            size="icon"
                            type="button"
                            disabled={isUploading}
                        >
                            <Paperclip className="size-4"/>
                            <span className="sr-only">Attach file</span>
                        </Button>

                        {status === 'streaming' ? (
                            <Button
                                onClick={() => { stop(); setIsGenerating(false); }}
                                type="button"
                                size="sm"
                                variant="destructive"
                                className="ml-auto gap-1.5"
                            >
                                <Square className="size-3.5"/>
                            </Button>
                        ) : (
                            <Button
                                disabled={(!input && files.length === 0) || isUploading}
                                type="submit"
                                size="sm"
                                className="ml-auto gap-1.5"
                            >
                                <ArrowUp className="size-3.5"/>
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
